import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaBell, FaBellSlash, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { useNotifications } from '../contexts/NotificationContext';
import { useAuth } from '../auth/AuthContext';

const NotificationSettings = () => {
  const { permission, isEnabled, isSupported, requestPermission, clearAll } = useNotifications();
  const { user } = useAuth();
  const [settings, setSettings] = useState({
    orderUpdates: true,
    promotions: true,
    paymentAlerts: true,
    shippingUpdates: true,
    returnUpdates: true
  });

  useEffect(() => {
    const savedSettings = localStorage.getItem('notificationSettings');
    if (savedSettings) {
      setSettings(JSON.parse(savedSettings));
    }
  }, []);

  const updateSetting = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    localStorage.setItem('notificationSettings', JSON.stringify(newSettings));
  };

  const handleEnableNotifications = async () => {
    await requestPermission();
  };

  if (!isSupported) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <FaBellSlash className="text-gray-400 text-xl" />
          <h2 className="text-xl font-bold text-gray-900">Push Notifications</h2>
        </div>
        <p className="text-gray-600">
          Push notifications are not supported in your browser. Please use a modern browser like Chrome, Firefox, or Edge.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
            isEnabled ? 'bg-green-100' : 'bg-gray-100'
          }`}>
            {isEnabled ? (
              <FaBell className="text-green-600 text-xl" />
            ) : (
              <FaBellSlash className="text-gray-400 text-xl" />
            )}
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Push Notifications</h2>
            <p className="text-sm text-gray-600">
              {isEnabled ? 'Notifications are enabled' : 'Enable to receive updates'}
            </p>
          </div>
        </div>
        {!isEnabled && (
          <button
            onClick={handleEnableNotifications}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Enable
          </button>
        )}
      </div>

      {permission === 'denied' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <FaTimesCircle className="text-red-600 text-xl flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-900 mb-1">Notifications Blocked</h3>
              <p className="text-sm text-red-700 mb-2">
                Notifications are currently blocked. Please enable them in your browser settings.
              </p>
              <p className="text-xs text-red-600">
                Go to: Browser Settings → Site Settings → Notifications → Allow
              </p>
            </div>
          </div>
        </div>
      )}

      {isEnabled && (
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-900 mb-3">Notification Preferences</h3>
          
          <div className="space-y-3">
            <motion.div
              whileHover={{ scale: 1.01 }}
              className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <FaCheckCircle className="text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Order Updates</p>
                  <p className="text-xs text-gray-600">Get notified when your order status changes</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.orderUpdates}
                  onChange={(e) => updateSetting('orderUpdates', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.01 }}
              className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                  <FaBell className="text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Promotions & Offers</p>
                  <p className="text-xs text-gray-600">Receive exclusive deals and special offers</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.promotions}
                  onChange={(e) => updateSetting('promotions', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.01 }}
              className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <FaCheckCircle className="text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Payment Alerts</p>
                  <p className="text-xs text-gray-600">Notifications for payment confirmations and refunds</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.paymentAlerts}
                  onChange={(e) => updateSetting('paymentAlerts', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.01 }}
              className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <FaCheckCircle className="text-orange-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Shipping Updates</p>
                  <p className="text-xs text-gray-600">Track your order delivery status</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.shippingUpdates}
                  onChange={(e) => updateSetting('shippingUpdates', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.01 }}
              className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                  <FaCheckCircle className="text-yellow-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Return Updates</p>
                  <p className="text-xs text-gray-600">Get notified about return and refund status</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.returnUpdates}
                  onChange={(e) => updateSetting('returnUpdates', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </motion.div>
          </div>

          <div className="pt-4 border-t border-gray-200 mt-4">
            <button
              onClick={clearAll}
              className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Clear All Notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationSettings;

